import { ShieldCheck, Eye, Zap, Lock, Handshake } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { site } from "@/config/site";

const values = [
  { icon: ShieldCheck, t: "Fiabilité", d: "Des engagements tenus et des informations vérifiées avant toute proposition." },
  { icon: Eye, t: "Transparence", d: "Des devis clairs, des conditions explicites, aucun frais caché." },
  { icon: Zap, t: "Réactivité", d: "Une première réponse sous 1 jour ouvré, un devis sous 48 h ouvrées." },
  { icon: Lock, t: "Confidentialité", d: "Vos informations ne sont jamais cédées à des tiers." },
  { icon: Handshake, t: "Respect des engagements", d: "Ce qui est écrit dans le devis est ce qui est livré." },
];

const identity = [
  ["Dénomination sociale", site.name],
  ["Forme juridique", site.legalForm],
  ["Capital social", site.capital],
  ["Siège social", site.address.full],
  ["RCS", site.rcs],
  ["Identifiant européen (EUID)", site.euid],
  ["TVA intracommunautaire", site.vat],
  ["Date d'immatriculation", site.registrationDate],
  ["Début d'activité", site.activityStart],
  ["Durée de la société", site.duration],
  ["Clôture de l'exercice social", site.fiscalYearEnd],
  ["Président", `${site.president.civility} ${site.president.fullName}`],
  ["Activité principale", site.activities.main],
];

export default function APropos() {
  return (
    <>
      <Seo
        title="À propos — MINES DE PARIS, SASU de négoce B2B à Pantin (93)"
        description="Présentation de MINES DE PARIS : SASU au capital de 1 000 €, siège à Pantin, RCS Bobigny 995 401 502, immatriculée le 22 décembre 2025. Mot du Président, valeurs et fiche d'identité complète de la société."
        path="/a-propos"
        breadcrumb="À propos"
      />
      <PageHero
        crumb="À propos"
        eyebrow="La société"
        lines={["Une société de négoce", "ancrée à Pantin,", "ouverte sur le monde"]}
        intro={`${site.name} est une ${site.legalFormShort} au capital de ${site.capital}, dont le siège est situé au ${site.address.full}. Immatriculée au RCS de ${site.rcsCity} sous le numéro ${site.rcsNumber} le ${site.registrationDate}, elle a débuté son activité le ${site.activityStart}.`}
      />

      <section className="py-16 sm:py-24" data-testid="positioning-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Objet social & positionnement</p>
            <h2 className="h-section mt-5 text-night">Commerce de gros et négoce interentreprises</h2>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-ink lg:col-span-7">
            <p>
              L'activité principale de {site.name} est le commerce de gros de produits non alimentaires et la fourniture de services
              interentreprises de négoce, en France et à l'étranger : achat-revente, sourcing, intermédiation commerciale et organisation
              de l'approvisionnement pour le compte de professionnels.
            </p>
            <p>
              L'objet social s'étend à toutes opérations industrielles, commerciales, financières, civiles, mobilières ou immobilières
              pouvant se rattacher directement ou indirectement à cet objet ou à tous objets similaires ou connexes.
            </p>
            <p>
              Notre positionnement est volontairement simple : un interlocuteur unique, une étude spécifique pour chaque demande et une
              proposition chiffrée claire. Nous ne revendiquons ni certification, ni label, ni référence qui ne soit pas vérifiable.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-night py-16 text-white sm:py-24 grain" data-testid="president-section">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative grid gap-12 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-9 lg:col-start-2">
            <p className="eyebrow">Mot du Président</p>
            <blockquote className="mt-6 space-y-5 text-base leading-relaxed text-white/85 md:text-lg">
              <p>
                « J'ai créé {site.name} avec une conviction simple : dans le négoce, la confiance se construit par la clarté des
                engagements et la qualité du suivi. Chaque demande que nous recevons fait l'objet d'une étude spécifique, d'une
                réponse rapide et d'une proposition sur mesure.
              </p>
              <p>
                Nous travaillons exclusivement avec des professionnels, en France et à l'international, avec la même exigence :
                des informations vérifiées, des conditions explicites et un interlocuteur joignable à chaque étape. »
              </p>
            </blockquote>
            <p className="mt-8 font-heading text-lg font-bold text-gold">{site.president.fullName}</p>
            <p className="text-sm text-white/60">{site.president.title}, {site.name}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="values-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Nos valeurs</p>
            <h2 className="h-section mt-5 text-night">Cinq principes qui guident chaque mission</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.07} className="card p-6">
                <v.icon className="h-6 w-6 text-gold" aria-hidden="true" />
                <h3 className="mt-5 font-heading text-base font-bold text-night md:text-lg">{v.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist py-16 sm:py-24" data-testid="identity-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Fiche d'identité</p>
            <h2 className="h-section mt-5 text-night">Informations légales de la société</h2>
          </Reveal>
          <Reveal delay={0.1} className="card mt-10 overflow-hidden">
            <table className="w-full text-sm" data-testid="identity-table">
              <caption className="sr-only">Fiche d'identité de {site.name}</caption>
              <tbody className="divide-y divide-line">
                {identity.map(([k, v]) => (
                  <tr key={k} className="grid gap-1 px-6 py-4 sm:grid-cols-12 sm:gap-6">
                    <th scope="row" className="text-left font-medium text-muted sm:col-span-4">{k}</th>
                    <td className="text-ink sm:col-span-8">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
